<template>
  <!--
    ATENÇÃO ao contrato do componente Modal da casa (src/ui/Modal.vue): ele
    recebe `show` (booleano) e `title` (texto), e avisa por `@close`/`@dismiss`.
    NÃO é `v-model:visible` + `:header`, que é a API do Dialog do PrimeVue.
    A primeira versão usava a API do PrimeVue — resultado: o modal NUNCA teve
    título, e nada acusou, porque prop desconhecida em Vue é ignorada em
    silêncio. Só apareceu quando uma mutação (M21) sobreviveu.
  -->
  <modal
    elem-id="chamados-modal"
    :show="isVisible"
    :title="cabecalho"
    :close-label="modo === 'novo' ? t('Cancelar') : t('Fechar')"
    :save-label="modo === 'novo' ? t('Abrir chamado') : undefined"
    :save-fn="enviarNovoChamado"
    :save-disabled-fn="novoChamadoBloqueado"
    :close-test-id="modo === 'novo' ? 'chamados-btn-cancelar' : ''"
    save-test-id="chamados-btn-abrir"
    size="large"
    data-testid="chamados-modal"
    @close="fechar"
    @dismiss="fechar"
  >
    <div v-if="isVisible" class="chamados-modal">
      <!-- ABRIR UM CHAMADO NOVO -->
      <ChamadosForm
        v-if="modo === 'novo'"
        ref="formRef"
        @criado="handleCriado"
      />

      <!-- O FIO DE UM CHAMADO — quatro seções que se distinguem pela
           superfície, não por caixas dentro de caixas:
             1. resumo  — o assunto manda; status, código e dados embaixo;
                          as ações do chamado (mudar status, ver a tela) à direita;
             2. agente  — painel na cor da marca: planejar ou resolver;
             3. conversa — o fio num poço cinza, os balões brancos sobre ele;
             4. resposta — painel branco com borda, o lugar de escrever. -->
      <template v-else-if="chamadoAberto">
        <header class="secao resumo" data-testid="chamados-resumo">
          <div class="resumo-linha">
            <h3 class="chamado-assunto">{{ chamadoAberto.assunto }}</h3>
            <AppButton
              v-if="chamadoAberto.pagina_uri"
              severity="secondary"
              size="small"
              text
              icon="pi pi-external-link"
              :label="t('Ver a tela')"
              :explanation="EX.seeScreen"
              data-testid="chamados-btn-ver-pagina"
              @click="handleIrParaPagina"
            />
          </div>

          <div class="chamado-meta">
            <span class="chamado-codigo" data-testid="chamados-detalhe-codigo">{{ chamadoAberto.codigo }}</span>
            <span><i class="pi pi-calendar"></i> {{ dataHora(chamadoAberto.created_at) }}</span>
            <span v-if="chamadoAberto.franquia_nome">
              <i class="pi pi-building"></i> {{ chamadoAberto.franquia_nome }}
            </span>
            <span v-if="chamadoAberto.aberto_por_nome" data-testid="chamados-aberto-por">
              <i class="pi pi-user"></i> {{ t('Aberto por') }}: {{ chamadoAberto.aberto_por_nome }}
            </span>
            <span><i class="pi pi-flag"></i> {{ t(rotuloPrioridade(chamadoAberto.prioridade)) }}</span>
          </div>

          <!--
            O STATUS como trilha do ciclo de vida: onde o chamado está, por onde
            já passou e — clicável, a UM clique — para onde ele pode ir. Quais
            etapas são clicáveis vem de `transicoes_possiveis`, calculado pelo
            BACKEND: a tela não tem cópia da máquina de estados, só desenha a
            ordem. Cancelar fica fora da trilha: é uma saída, não uma etapa.
          -->
          <div class="ciclo" role="group" :aria-label="t('Status do chamado')">
            <StatusTag
              v-if="!ETAPAS_DO_CICLO.includes(chamadoAberto.status ?? '')"
              v-explain.top="stepExplanation(chamadoAberto.status ?? '')"
              tabindex="0"
              :severity="severidadeDoStatus(chamadoAberto.status)"
              :value="t(rotuloStatus(chamadoAberto.status))"
              data-testid="chamados-detalhe-status"
            />
            <ol class="ciclo-trilha">
              <li v-for="(etapa, i) in ETAPAS_DO_CICLO" :key="etapa" class="ciclo-passo">
                <button
                  v-if="transicoesPossiveis.includes(etapa)"
                  v-explain.top="stepExplanation(etapa)"
                  type="button"
                  class="ciclo-etapa ciclo-etapa--disponivel"
                  :data-testid="`chamados-btn-status-${etapa}`"
                  @click="handleMudarStatus(etapa)"
                >
                  {{ t(rotuloStatus(etapa)) }}
                </button>
                <span
                  v-else
                  v-explain.top="stepExplanation(etapa)"
                  tabindex="0"
                  class="ciclo-etapa"
                  :class="{
                    'ciclo-etapa--atual': etapa === chamadoAberto.status,
                    'ciclo-etapa--passada': i < indiceNoCiclo,
                    [`ciclo-etapa--${severidadeDoStatus(etapa)}`]: etapa === chamadoAberto.status,
                  }"
                  :aria-current="etapa === chamadoAberto.status ? 'step' : undefined"
                  :data-testid="etapa === chamadoAberto.status ? 'chamados-detalhe-status' : undefined"
                >
                  <i v-if="i < indiceNoCiclo" class="pi pi-check" aria-hidden="true"></i>
                  {{ t(rotuloStatus(etapa)) }}
                </span>
              </li>
            </ol>

            <AppButton
              v-for="destino in transicoesForaDoCiclo"
              :key="destino"
              class="ciclo-saida"
              severity="secondary"
              size="small"
              text
              :label="destino === 'cancelado' ? t('Cancelar chamado') : t(rotuloStatus(destino))"
              :explanation="exitExplanation(destino)"
              :data-testid="`chamados-btn-status-${destino}`"
              @click="handleMudarStatus(destino)"
            />
          </div>
        </header>

        <!--
          2. O AGENTE DE CHAMADOS — logo abaixo do resumo, porque é uma decisão
          sobre o chamado inteiro (planejar? resolver?) e não um apêndice do
          fio. Os pedidos vêm do BACKEND (`pedidos_de_agente`): a tela não sabe
          quem pode marcar. A marca atual só chega para o suporte; o
          solicitante recebe null e esta seção não aparece para ele.
        -->
        <section
          v-if="chamadoAberto?.agente || pedidosDeAgente.length"
          class="secao agente"
          :class="{ 'agente--marcado': chamadoAberto?.agente }"
          data-testid="chamados-agente"
        >
          <span class="agente-avatar" aria-hidden="true"><i class="pi pi-sparkles"></i></span>
          <span class="agente-nome">{{ t('Agente') }}</span>
          <span v-if="chamadoAberto?.agente" class="agente-marca" data-testid="chamados-agente-marca">
            {{ t(rotuloPedidoDeAgente(chamadoAberto.agente)) }}
            <template v-if="chamadoAberto.agente_marcado_por"> · {{ chamadoAberto.agente_marcado_por }}</template>
            <template v-if="chamadoAberto.agente_marcado_at"> · {{ dataHora(chamadoAberto.agente_marcado_at) }}</template>
          </span>
          <span
            v-if="chamadoAberto?.agente_esforco"
            v-explain.top="effortExplanation(chamadoAberto.agente_esforco)"
            class="agente-esforco"
            tabindex="0"
            data-testid="chamados-agente-esforco"
          >
            {{ t('Esforço') }} <strong>{{ chamadoAberto.agente_esforco }}</strong>
          </span>

          <span class="agente-acoes">
            <button
              v-for="pedido in pedidosOrdenados"
              :key="pedido"
              v-explain.top="agentRequestExplanation(pedido)"
              type="button"
              class="agente-pedido"
              :disabled="!!agentRequestBlocked"
              :data-testid="`chamados-btn-agente-${pedido}`"
              @click="handleMarcarAgente(pedido)"
            >
              <i :class="ACAO_DO_AGENTE[pedido]?.icone ?? 'pi pi-sparkles'" aria-hidden="true"></i>
              {{ t(ACAO_DO_AGENTE[pedido]?.titulo ?? rotuloAcaoDeAgente(pedido)) }}
            </button>
            <AppButton
              v-if="chamadoAberto?.agente && souSuporte"
              severity="secondary"
              size="small"
              text
              icon="pi pi-times"
              :label="t('Tirar a marca')"
              :explanation="EX.unmark"
              data-testid="chamados-btn-agente-limpar"
              @click="handleMarcarAgente('')"
            />
          </span>
        </section>

        <!-- CONVERSAÇÃO -->
        <section class="secao conversa">
          <div class="secao-topo conversa-topo">
            <h4>
              <i class="pi pi-comments"></i>
              {{ t('Conversação') }}
              <AppBadge :value="mensagens.length" severity="secondary" />
            </h4>
            <AppButton
              icon="pi pi-refresh"
              severity="secondary"
              size="small"
              rounded
              outlined
              :loading="carregando"
              :label="t('Atualizar')"
              :explanation="EX.refresh"
              data-testid="chamados-btn-atualizar-fio"
              @click="handleAtualizarFio"
            />
          </div>

          <!--
            A JANELA DE CONVERSA: o fio em cima e a caixa de resposta colada na
            borda de baixo, como num aplicativo de mensagem. Uma moldura só —
            quem escreve vê a última mensagem logo acima do que escreve.
          -->
          <div class="chat">
          <div ref="fioEl" class="fio" data-testid="chamados-fio">
            <div
              v-for="msg in mensagens"
              :key="msg.id"
              class="balao-wrapper"
              :class="msg.autor_sou_eu ? 'balao-wrapper--eu' : 'balao-wrapper--outro'"
              :data-testid="`chamados-mensagem-${msg.id}`"
            >
              <div
                class="balao"
                :class="{
                  'balao--eu': msg.autor_sou_eu,
                  'balao--interna': msg.interna,
                }"
              >
                <!--
                  A tarja de NOTA INTERNA só existe para o suporte: o backend
                  não manda a linha para o solicitante. O rótulo é para o
                  atendente NÃO se confundir sobre o que o franqueado leu.
                -->
                <div v-if="msg.interna" class="tarja-interna" data-testid="chamados-tarja-interna">
                  <i class="pi pi-eye-slash"></i>
                  {{ t('Nota interna — o solicitante não vê') }}
                </div>

                <div class="balao-meta">
                  <!--
                    Avatar como <span> próprio, e não `primevue/avatar`: não há
                    wrapper em src/ui/ para ele, e importar PrimeVue direto num
                    domínio é o que a regra `no-raw-inputs` proíbe. Um círculo
                    com ícone resolve sem criar dependência nova.
                  -->
                  <span
                    class="balao-avatar"
                    :class="msg.autor_e_suporte ? 'balao-avatar--suporte' : 'balao-avatar--solicitante'"
                    aria-hidden="true"
                  >
                    <i :class="msg.autor_e_suporte ? 'pi pi-headphones' : 'pi pi-user'"></i>
                  </span>
                  <span class="balao-autor" :data-testid="`chamados-mensagem-autor-${msg.id}`">
                    {{ nomeDoAutor(msg) }}
                  </span>
                  <span class="balao-hora">{{ dataHora(msg.created_at) }}</span>
                </div>

                <!--
                  `v-html` porque o corpo agora é HTML do editor (negrito,
                  listas, links) — o mesmo que a tela do Odoo faz com
                  `message.body`. SEMPRE passando pelo DOMPurify: o autor é
                  um usuário autenticado, mas "interno" não é o mesmo que
                  "confiável", e código colado de uma página qualquer rodaria
                  na sessão de quem lê o chamado. Ver corpoSanitizado().

                  (Este comentário é renderizado no DOM: nada de exemplos de
                  marcação perigosa aqui, ou o próprio teste de sanitização
                  fica vermelho ao encontrá-los no balão. Aconteceu.)
                -->
                <div class="balao-corpo" :class="{ 'balao-corpo--texto': ehTextoPuro(msg.corpo) }" v-html="corpoSanitizado(msg.corpo)"></div>

                <!--
                  As imagens da mensagem. Abrem em aba nova em vez de num
                  lightbox próprio: o navegador já sabe dar zoom, girar e
                  salvar, e o suporte quase sempre quer a imagem ao LADO da
                  tela do sistema, não por cima dela.
                -->
                <div v-if="msg.anexos?.length" class="balao-anexos">
                  <a
                    v-for="anexo in msg.anexos"
                    :key="anexo.id"
                    :href="anexo.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="balao-anexo"
                    :data-testid="`chamados-anexo-${anexo.id}`"
                  >
                    <img :src="anexo.url" :alt="anexo.nome || t('Imagem do chamado')" />
                  </a>
                </div>
              </div>
            </div>
          </div>

        <!-- RESPOSTA -->
        <!--
          A RESPOSTA é uma caixa de conversa (InputRichText `composer`): sem
          título nem rótulo — a caixa se explica. Enter envia, Shift+Enter
          quebra a linha; formatação recolhida atrás do "Aa"; a imagem no pé da
          caixa; as miniaturas dentro dela.
        -->
        <div v-if="podeResponder" class="resposta" :class="{ 'resposta--interna': interna }">
          <!--
            O aviso fica ao LADO do rascunho, não num toast que some: quem lê
            precisa saber que o texto é de máquina E que ele não considerou as
            notas internas, no momento em que decide enviar.
          -->
          <!--
            A LEITURA DA IA — apoio, não veredito.
            Fica ABAIXO do campo e recolhida por padrão, de propósito: um
            rótulo em destaque no topo ("é cobrança") enviesa o atendente ANTES
            de ele ler o fio, e um rótulo errado com destaque é pior que
            nenhum. Enquanto ninguém mediu se a classificação acerta, ela é
            informação de apoio — e a confiança aparece junto.
          -->
          <details v-if="analise" class="analise" data-testid="chamados-analise">
            <summary>
              <i class="pi pi-sparkles"></i>
              {{ t('Leitura da IA') }}:
              <strong>{{ t(ROTULO_INTENCAO[analise.intencao] ?? analise.intencao) }}</strong>
              <span class="analise-confianca" :class="`analise-confianca--${analise.confianca}`">
                {{ t('confiança') }} {{ t(analise.confianca) }}
              </span>
            </summary>

            <p class="analise-resumo">{{ analise.resumo }}</p>

            <div v-if="analise.falta.length" class="analise-falta">
              <span>{{ t('Falta para resolver') }}:</span>
              <ul>
                <li v-for="(item, i) in analise.falta" :key="i">{{ item }}</li>
              </ul>
            </div>
          </details>

          <div v-if="veioDeSugestao" class="aviso-sugestao" data-testid="chamados-aviso-sugestao">
            <i class="pi pi-info-circle"></i>
            {{ t('Rascunho gerado por IA — revise antes de enviar. Ele não leu as notas internas.') }}
          </div>

          <!-- Os MESMOS controles da abertura — formatação (atrás do "Aa"),
               imagem, colar e arrastar. A imagem sai pelo `@image-file` e vira
               anexo no Blob, nunca base64 dentro de `corpo`. -->
          <InputRichText
            v-model="corpo"
            variant="composer"
            elem-id="chamados-resposta"
            :label="interna ? t('Nota interna') : t('Sua resposta')"
            :placeholder="interna
              ? t('Escreva uma nota para a equipe — o solicitante não vê')
              : t('Escreva sua resposta… cole ou arraste imagens')"
            :show-toolbar="true"
            :external-images="true"
            :disabled="carregando"
            :explanation="carregando ? EX.sending : null"
            :submit-label="interna ? t('Salvar nota') : t('Enviar')"
            :submit-explanation="sendExplanation"
            :submit-disabled="!podeEnviarResposta"
            :submit-loading="carregando"
            submit-test-id="chamados-btn-enviar"
            data-testid="chamados-input-resposta"
            @image-file="receberImagem"
            @submit="handleResponder"
          >
            <!--
              O MODO da mensagem, no topo da caixa — só para o suporte: para o
              solicitante uma "nota que ele mesmo não vê" não significa nada, e
              o backend força `interna = false` de qualquer forma. Abas e não
              um interruptor: escrever ao franqueado o que era para a equipe é
              o erro caro aqui, e o modo precisa estar onde se escreve.
            -->
            <template v-if="souSuporte" #cabecalho>
              <div class="resposta-modos" role="tablist" :aria-label="t('Tipo de mensagem')" data-testid="chamados-toggle-interna">
                <button
                  v-explain.top="EX.replyTab"
                  type="button"
                  role="tab"
                  class="resposta-modo"
                  :class="{ 'resposta-modo--ativo': !interna }"
                  :aria-selected="!interna"
                  data-testid="chamados-modo-resposta"
                  @click="interna = false"
                >
                  <i class="pi pi-reply"></i> {{ t('Responder') }}
                </button>
                <button
                  v-explain.top="EX.noteTab"
                  type="button"
                  role="tab"
                  class="resposta-modo resposta-modo--nota"
                  :class="{ 'resposta-modo--ativo': interna }"
                  :aria-selected="interna"
                  data-testid="chamados-modo-nota"
                  @click="interna = true"
                >
                  <i class="pi pi-eye-slash"></i> {{ t('Nota interna') }}
                </button>
              </div>
            </template>

            <template #rodape>
              <ChamadosImagePicker
                ref="pickerRef"
                v-model="anexos"
                :disabled="carregando"
                :explanation="carregando ? EX.sending : null"
                so-miniaturas
                testid="chamados-anexo-resposta"
              />
            </template>

            <!--
              Só para o suporte, porque só ele responde chamado — e o backend
              recusa com 403 de qualquer forma. Mostrar o botão a quem não pode
              usá-lo seria oferecer uma porta trancada.
            -->
            <template v-if="souSuporte" #acoes>
              <button
                v-explain.top="suggestBlocked ?? EX.suggest"
                type="button"
                class="resposta-sugerir"
                :disabled="!!suggestBlocked"
                data-testid="chamados-btn-sugerir"
                @click="handleSugerir"
              >
                <i :class="sugerindo ? 'pi pi-spin pi-spinner' : 'pi pi-sparkles'"></i>
                {{ sugerindo ? t('Escrevendo...') : t('Sugerir') }}
              </button>
            </template>
          </InputRichText>
        </div>
          </div>
        </section>

      </template>
    </div>
  </modal>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

// Wrappers da casa, NUNCA PrimeVue direto: a regra `no-raw-inputs` existe
// para que uma troca de biblioteca aconteça em src/ui/ e não em 30 domínios.
// Eu importei `primevue/*` na primeira versão e o qa-file pegou.
import AppBadge from '../../ui/Badge.vue';
import StatusTag from '../../ui/StatusTag.vue';

import AppButton from '../../ui/AppButton.vue';
import type { Explanation, ExplanationNow, ExplanationTone } from '../../explanations/types';
import { EFFORT_MEANING, STATUS_MEANING, ticketExplanations } from './explanations';
import ChamadosForm from './ChamadosForm.vue';
import ChamadosImagePicker from './ChamadosImagePicker.vue';
import InputRichText from '../../inputs/InputRichText.vue';
import { sanitizeRichText } from '../../utils/sanitizeRichText';
import type { AnaliseDeChamado } from '../../composables/aiAgentContract';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useChamados } from '../../composables/useChamados';
import { useDateFormatter } from '../../composables/useDateFormatter';

/**
 * ChamadosModal — abre em dois modos: `novo` (o formulário) e `detalhe` (o fio
 * da conversa). Registrado UMA vez em ModalManager.vue e aberto por eventBus,
 * como todos os modais do sistema.
 *
 * Dois modos num modal só, e não dois modais, porque abrir um chamado leva
 * direto ao fio dele: o `@criado` do formulário troca o modo em vez de fechar e
 * reabrir outra coisa. Fechar a janela depois de abrir um chamado deixaria o
 * usuário sem saber se deu certo.
 */

type Modo = 'novo' | 'detalhe';

const { t } = useI18n();
const router = useRouter();

/** The tickets catalog (./explanations.ts), rebuilt when the language changes. */
const EX = computed(() => ticketExplanations(t));
const { fromApiDateTimeFormat, formatDateTime } = useDateFormatter();

/** ISO 8601 UTC -> dd/mm/aaaa hh:mm:ss. Ver o mesmo helper em ChamadosTable. */
const dataHora = (iso?: string | null): string => {
  if (!iso) return '';

  const d = fromApiDateTimeFormat(iso);

  return d ? formatDateTime(d) : '';
};
const {
  carregando,
  chamadoAberto,
  mensagens,
  transicoesPossiveis,
  pedidosDeAgente,
  souSuporte,
  abrirDetalhe,
  responder,
  mudarStatus,
  marcarAgente,
  anexarNaUltimaMensagem,
  sugerirResposta,
  fecharDetalhe,
} = useChamados();

const isVisible = ref(false);
const modo = ref<Modo>('detalhe');

/**
 * O FORMULÁRIO DE ABERTURA É COMANDADO PELO RODAPÉ DA JANELA (chamado
 * 2026-0031), e não por botões dentro do corpo: "Cancelar" e "Abrir chamado"
 * são o par padrão do `src/ui/Modal.vue`, cujo rodapé é dele — o Modal não
 * aceita um slot de rodapé próprio. Daí o ref: o modal precisa mandar o formulário
 * enviar e perguntar se o botão pode acender.
 */
const formRef = ref<InstanceType<typeof ChamadosForm> | null>(null);

const enviarNovoChamado = async (): Promise<void> => {
  await formRef.value?.submit();
};

/*
 * Sem o formulário montado (modo detalhe, ou antes do primeiro tick) o botão
 * fica desabilitado — acender um "Abrir chamado" que não tem formulário atrás
 * seria pior do que não mostrá-lo.
 */
const novoChamadoBloqueado = (): boolean => !formRef.value?.podeEnviar();
const corpo = ref('');
const interna = ref(false);
const anexos = ref<File[]>([]);
const sugerindo = ref(false);

/**
 * O rascunho exatamente como a IA o entregou.
 *
 * O aviso "gerado por IA" é DERIVADO de comparar o campo com este valor, e não
 * de um booleano que alguém precisa lembrar de desligar. Assim que o atendente
 * muda uma vírgula, o texto passa a ser dele e o aviso some sozinho —
 * continuar avisando depois da revisão seria mentira, e um booleano manual
 * erraria justamente no caso de o usuário editar e não enviar.
 */
const textoSugerido = ref('');

/**
 * A leitura que a IA fez do chamado. `null` = não veio, e a tela simplesmente
 * não mostra o painel — o rascunho sozinho continua útil.
 */
const analise = ref<AnaliseDeChamado | null>(null);

/** Rótulo humano da intenção. A chave crua não diz nada para o atendente. */
const ROTULO_INTENCAO: Record<string, string> = {
  duvida: 'Dúvida de uso',
  bug: 'Possível defeito',
  liberacao: 'Pedido de liberação',
  cobranca: 'Cobrança',
  melhoria: 'Sugestão de melhoria',
  outro: 'Outro',
};

const veioDeSugestao = computed(
  () => textoSugerido.value !== '' && corpo.value === textoSugerido.value,
);

const pickerRef = ref<InstanceType<typeof ChamadosImagePicker> | null>(null);

/** Imagem colada/arrastada dentro do editor cai na MESMA lista do seletor. */
const receberImagem = (arquivo: File): void => {
  pickerRef.value?.receber(arquivo);
};

/**
 * O corpo da mensagem, pronto para o `v-html`.
 *
 * Mensagens antigas são TEXTO PURO (o campo era um textarea até esta fatia) e
 * passam intactas pelo sanitizador — não há migração a fazer, os dois formatos
 * convivem.
 *
 * `sanitizeRichText` e não `DOMPurify.sanitize` direto: a versão instalada tem
 * um furo medido em que um `<` + script no conteúdo desarma a limpeza do que
 * vem depois. O utilitário limpa até estabilizar. Leia o docblock dele.
 */
const corpoSanitizado = (html?: string | null): string => sanitizeRichText(html);

/**
 * Texto puro (mensagem antiga, de antes do editor) guarda as próprias quebras
 * de linha — só ele leva `pre-wrap`. Num corpo HTML a quebra entre tags não é
 * conteúdo, e `pre-wrap` a transformaria em linha em branco (o plano que o
 * agente escreve tem dezenas delas).
 */
const ehTextoPuro = (corpo?: string | null): boolean =>
  !/<(p|div|ul|ol|h[1-6]|table|pre|blockquote|details|br)\b/i.test(String(corpo ?? ''));

/**
 * Só imagem, sem texto, é resposta válida — é o caso mais comum do suporte
 * ("segue o print"). Exigir texto obrigaria a pessoa a digitar "segue" para o
 * botão acender.
 */
const semTags = (html: string): string => html.replace(/<[^>]*>/g, '').trim();

const podeEnviarResposta = computed(
  () => !carregando.value && (semTags(corpo.value) !== '' || anexos.value.length > 0),
);

const ROTULOS: Record<string, string> = {
  aberto: 'Aberto',
  em_atendimento: 'Em atendimento',
  aguardando_solicitante: 'Aguardando o solicitante',
  resolvido: 'Resolvido',
  fechado: 'Fechado',
  cancelado: 'Cancelado',
};

/*
 * Os helpers aceitam `undefined` porque o CONTRATO GERADO marca `status`,
 * `prioridade` etc. como opcionais (`?: string`). O vue-tsc pegou isso — e a
 * correção certa é o helper tolerar a forma real do dado, não um `!` no
 * template escondendo que o campo pode faltar.
 */
const rotuloStatus = (s?: string): string => (s ? (ROTULOS[s] ?? s) : '');

const ROTULOS_PRIORIDADE: Record<string, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
  critica: 'Crítica',
};

const rotuloPrioridade = (p?: string): string => (p ? (ROTULOS_PRIORIDADE[p] ?? p) : '');

/**
 * A cor da tarja de status. Mapa de APRESENTAÇÃO — a regra de quais status
 * existem continua no backend (`ChamadoStatus`); aqui só se decide a cor.
 */
const SEVERIDADE: Record<string, string> = {
  aberto: 'info',
  em_atendimento: 'warn',
  aguardando_solicitante: 'secondary',
  resolvido: 'success',
  fechado: 'contrast',
  cancelado: 'danger',
};

const severidadeDoStatus = (s?: string): string => (s ? (SEVERIDADE[s] ?? 'secondary') : 'secondary');

/**
 * A ordem em que a trilha do status é desenhada — o caminho feliz do
 * chamado (ChamadoStatus.php). Só a ORDEM mora aqui; quais etapas são
 * clicáveis vem de `transicoes_possiveis`, do backend.
 */
const ETAPAS_DO_CICLO = ['aberto', 'em_atendimento', 'aguardando_solicitante', 'resolvido', 'fechado'];

/** Onde o chamado está na trilha (-1 quando está fora dela: cancelado). */
const indiceNoCiclo = computed((): number => ETAPAS_DO_CICLO.indexOf(chamadoAberto.value?.status ?? ''));

/** Transições que não são etapas da trilha — hoje, só cancelar. */
const transicoesForaDoCiclo = computed((): string[] =>
  transicoesPossiveis.value.filter((d) => !ETAPAS_DO_CICLO.includes(d)),
);

const ENCERRADOS = ['fechado', 'cancelado'];

/**
 * WHY a step is not available, in plain words. Only the explanation: what is
 * clickable still comes from `transicoes_possiveis` (the backend) — if it
 * allows something this sentence did not foresee, the step becomes a button
 * and this function is not even called for it.
 */
const blockedReason = (step: string, current: string): string => {
  if (!souSuporte.value) return t('Só a equipe de suporte muda o status — você acompanha por aqui.');
  if (ENCERRADOS.includes(current)) return t('O chamado está encerrado e não muda mais de status.');
  if (step === 'aberto') return t('Um chamado não volta para «Aberto»: depois de atendido, ele segue em frente.');
  if (step === 'fechado') return t('Só se fecha um chamado que já foi resolvido.');
  if (current === 'aberto') return t('Primeiro alguém precisa atender: passe para «Em atendimento».');
  return t('Não dá para ir de «{de}» direto para cá.', { de: t(rotuloStatus(current)) });
};

/**
 * The Explanation of one step of the stepper: what the status means and what
 * a click does — or why it cannot. The first consumer of the Explanation
 * pieces (it was `dicaDaEtapa`, with its own HTML and CSS).
 */
const stepExplanation = (step: string): Explanation => {
  const current = chamadoAberto.value?.status ?? '';
  const meaning = STATUS_MEANING[step];
  const index = ETAPAS_DO_CICLO.indexOf(step);

  let now: ExplanationNow;
  if (step === current) {
    now = { kind: 'current', text: t('Você está aqui.') };
  } else if (transicoesPossiveis.value.includes(step)) {
    now = { kind: 'yes', text: t('Clique para mover o chamado para cá.') };
  } else if (index !== -1 && index < indiceNoCiclo.value) {
    now = { kind: 'no', text: t('Já passou por aqui.') + ' ' + blockedReason(step, current) };
  } else {
    now = { kind: 'no', text: blockedReason(step, current) };
  }

  return {
    title: t(rotuloStatus(step)),
    icon: meaning?.icon ?? 'pi pi-circle',
    tone: severidadeDoStatus(step) as ExplanationTone,
    what: meaning ? t(meaning.text) : t(rotuloStatus(step)),
    now,
  };
};

/** The exits off the stepper (today, only cancel): cancelling has its own warning. */
const exitExplanation = (target: string): Explanation =>
  target === 'cancelado' ? EX.value.cancelTicket : stepExplanation(target);

/** The effort pill: the scale, with the estimate of THIS ticket as the title. */
const effortExplanation = (size: string): Explanation => ({
  ...EX.value.effort,
  title: `${t('Esforço')} ${size}` + (EFFORT_MEANING[size] ? ` — ${t(EFFORT_MEANING[size])}` : ''),
});

/** O container do fio, para rolar até a última mensagem. */
const fioEl = ref<HTMLElement | null>(null);

/**
 * Rola para o fim do fio.
 *
 * Sem isto, um chamado com histórico abre mostrando a PRIMEIRA mensagem — e a
 * resposta que a pessoa veio ler está lá embaixo, fora da vista. Ela acha que
 * ninguém respondeu.
 *
 * `nextTick` porque a rolagem só faz sentido depois que os balões existem no
 * DOM; medir a altura antes disso devolve zero.
 */
const rolarParaOFim = async (): Promise<void> => {
  await nextTick();
  if (fioEl.value) {
    fioEl.value.scrollTop = fioEl.value.scrollHeight;
  }
};

/** Recarrega o fio sem fechar o modal — o irmão do botao de refresh do Odoo. */
const handleAtualizarFio = async (): Promise<void> => {
  const id = chamadoAberto.value?.id;
  if (!id) return;

  try {
    await abrirDetalhe(id);
    await rolarParaOFim();
  } catch (e) {
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit('globalMessage.error', t('Erro ao atualizar a conversa') + ': ' + detalhe);
  }
};

/**
 * Quem escreveu a mensagem.
 *
 * `autor_nome` é gravado no backend no momento da escrita. Pode vir vazio em
 * mensagem antiga (a coluna nasceu depois do domínio), e nesse caso o fallback
 * é o PAPEL — "Suporte" ou "Solicitante" —, nunca em branco: um balão sem
 * autor num fio de conversa parece defeito, e o leitor não tem como saber se é
 * dado faltando ou bug.
 */
const nomeDoAutor = (msg: { autor_nome?: string | null; autor_e_suporte?: boolean }): string => {
  const nome = (msg.autor_nome ?? '').trim();
  if (nome !== '') return nome;

  return msg.autor_e_suporte ? t('Suporte') : t('Solicitante');
};

/**
 * Vai para a tela de origem do chamado.
 *
 * O modal FECHA antes de navegar: deixá-lo aberto por cima da tela de destino
 * esconderia justamente o que a pessoa quer olhar.
 */
const handleIrParaPagina = (): void => {
  const destino = chamadoAberto.value?.pagina_uri;
  if (!destino) return;

  log('ChamadosModal', 'handleIrParaPagina', 'Indo para a tela de origem', destino);
  fechar();
  void router.push(destino);
};

/**
 * O título carrega o CÓDIGO do chamado — "Chamado 2026-0007", não só
 * "Chamado".
 *
 * Com várias abas abertas (o suporte trabalha assim), um título genérico faz
 * todas parecerem a mesma janela. O código é o que a pessoa dita ao telefone e
 * o que ela procura na lista.
 */
const cabecalho = computed(() => {
  if (modo.value === 'novo') return t('Novo chamado');

  const codigo = chamadoAberto.value?.codigo;

  return codigo ? `${t('Chamado')} ${codigo}` : t('Chamado');
});

/** Chamado encerrado não recebe mensagem — o backend recusa, a tela não oferece. */
const podeResponder = computed(() => {
  const status = chamadoAberto.value?.status ?? '';

  return status !== 'fechado' && status !== 'cancelado';
});

const fechar = (): void => {
  isVisible.value = false;
  corpo.value = '';
  interna.value = false;
  fecharDetalhe();
};

const handleAbrir = async (payload?: { modo?: Modo; id?: number }): Promise<void> => {
  // SEM PAYLOAD SIGNIFICA "NOVO", não "detalhe de coisa nenhuma".
  //
  // O atalho de teclado (ctrl/cmd + .) emite `modal.chamados.open` SEM
  // argumento — `useKeyboardShortcuts` faz `eventBus.emit(shortcut.action)` e
  // não tem como passar payload. Com o default anterior ('detalhe'), o atalho
  // abria um modal vazio: sem formulário, sem fio, sem explicação.
  //
  // Derivar o modo do que CHEGOU, em vez de fixar um default, faz os dois
  // chamadores funcionarem sem que nenhum precise saber do outro.
  modo.value = payload?.modo ?? (payload?.id ? 'detalhe' : 'novo');
  corpo.value = '';
  interna.value = false;
  isVisible.value = true;

  if (modo.value === 'detalhe' && payload?.id) {
    try {
      await abrirDetalhe(payload.id);
      await rolarParaOFim();
    } catch (e) {
      const detalhe = e instanceof Error ? e.message : String(e);
      eventBus.emit('globalMessage.error', t('Erro ao carregar o chamado') + ': ' + detalhe);
      isVisible.value = false;
    }
  }
};

const handleCriado = async (id: number): Promise<void> => {
  log('ChamadosModal', 'handleCriado', 'Chamado criado, indo para o fio', id);

  /*
   * CARREGA O DETALHE — não basta trocar o modo.
   *
   * `useChamados()` cria refs POR INSTÂNCIA, e o formulário tem a dele. O
   * detalhe que o `abrirChamado()` guardou ficou lá dentro; o `chamadoAberto`
   * DESTE componente continuava nulo, então o `v-else-if` não renderizava
   * nada: um modal com o título "Chamado" sem número, sem conteúdo, só com o
   * botão Fechar — logo depois de a pessoa descrever um problema.
   *
   * Nada disso falha de forma visível para quem programa: sem erro no
   * console, sem requisição vermelha, e o chamado É criado no banco. Só a
   * tela mente.
   *
   * `await` ANTES de trocar o modo: inverter a ordem mostraria a caixa vazia
   * durante a viagem da requisição — o mesmo sintoma, mais curto.
   */
  try {
    await abrirDetalhe(id);
    modo.value = 'detalhe';
    await rolarParaOFim();
  } catch (e) {
    // O chamado FOI criado. Não conseguir exibi-lo é irritante; sugerir que
    // não foi criado seria pior — a pessoa abriria outro.
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit(
      'globalMessage.error',
      t('O chamado foi aberto, mas não consegui exibir a conversa') + ': ' + detalhe,
    );
    fechar();
  }

  eventBus.emit('chamados.refresh');
};

/**
 * Why "Sugerir" is blocked right now, or null when it is available.
 *
 * The suggestion REPLACES the field — so it is only offered on an empty one:
 * throwing away what the agent already typed would lose work in one click.
 * This used to be an error toast AFTER the click; now the button is disabled
 * and says so before it (The Self-Explaining Rule, the "Sugerir" example).
 */
const suggestBlocked = computed((): Explanation | null => {
  if (sugerindo.value) return { ...EX.value.suggest, now: { kind: 'no', text: t('A IA está escrevendo o rascunho — aguarde.') } };
  if (carregando.value) return EX.value.sending;
  if (semTags(corpo.value) !== '') {
    return { ...EX.value.suggest, now: { kind: 'no', text: t('Apague o texto atual: a sugestão substituiria o que você escreveu.') } };
  }

  return null;
});

/**
 * The send button: what it does in the current mode, or — when disabled —
 * why. The reason lives here, next to `podeEnviarResposta`, the rule that
 * disables it: one rule, one sentence per reason.
 */
const sendExplanation = computed((): Explanation => {
  const base = interna.value ? EX.value.saveNote : EX.value.send;
  if (carregando.value) return { ...base, now: EX.value.sending.now };
  if (!podeEnviarResposta.value) return { ...base, now: { kind: 'no', text: t('A mensagem está vazia: escreva algo ou anexe uma imagem.') } };

  return base;
});

/**
 * Traz o rascunho e o coloca no campo de resposta.
 *
 * SUBSTITUI o que estiver escrito — por isso só existe com o campo vazio
 * (ver `suggestBlocked`).
 */
const handleSugerir = async (): Promise<void> => {
  const id = chamadoAberto.value?.id;
  if (!id || suggestBlocked.value) return;

  sugerindo.value = true;

  try {
    const { sugestao: texto, analise: lida } = await sugerirResposta(id);
    analise.value = lida ?? null;

    // O modelo devolve texto puro; o editor guarda HTML. Cada linha vira um
    // parágrafo — sem isso o rascunho chega como um bloco só, e o atendente
    // reformata na mão o que a IA já tinha separado.
    corpo.value = texto
      .split(/\n{2,}/)
      .map((par) => `<p>${escaparHtml(par).replace(/\n/g, '<br>')}</p>`)
      .join('');

    textoSugerido.value = corpo.value;
    log('ChamadosModal', 'handleSugerir', 'Rascunho aplicado', { id, chars: texto.length });
  } catch (e) {
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit('globalMessage.error', t('Não foi possível gerar a sugestão') + ': ' + detalhe);
  } finally {
    sugerindo.value = false;
  }
};

/**
 * Escapa o texto do modelo antes de embrulhá-lo em `<p>`.
 *
 * O modelo devolve TEXTO, e é instruído a não usar marcação — mas "instruído"
 * não é "garantido". Um `<` vindo dele viraria marcação de verdade dentro do
 * editor, e daí em diante seria conteúdo do chamado. Escapar aqui custa nada.
 */
const escaparHtml = (texto: string): string =>
  texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const handleResponder = async (): Promise<void> => {
  const id = chamadoAberto.value?.id;
  if (!id) return;

  try {
    // A mensagem PRIMEIRO, as imagens depois: é ela que dá dono ao anexo.
    //
    // Só-imagem manda um texto REAL, e não vazio: `Chamados_ResponderInput`
    // recusa `corpo` em branco ("corpo is required"), e afrouxar essa
    // validação para acomodar um caso de tela seria trocar uma garantia do
    // contrato por conveniência. O texto abaixo descreve o que de fato foi
    // enviado — não é um preenchimento falso para enganar o validador.
    const texto = semTags(corpo.value) !== ''
      ? corpo.value
      : t('Imagem anexada');

    await responder(id, texto, interna.value);

    if (anexos.value.length > 0) {
      // Mesma escolha do formulário de abertura: falha de imagem NÃO desfaz
      // nem esconde a mensagem, que já foi enviada e já está sendo lida.
      try {
        await anexarNaUltimaMensagem(id, anexos.value);
      } catch (e) {
        const detalhe = e instanceof Error ? e.message : String(e);
        eventBus.emit(
          'globalMessage.error',
          t('A mensagem foi enviada, mas a imagem não subiu') + ': ' + detalhe,
        );
      }
    }

    corpo.value = '';
    interna.value = false;
    anexos.value = [];
    textoSugerido.value = '';
    analise.value = null;
    await rolarParaOFim();
    eventBus.emit('chamados.refresh');
  } catch (e) {
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit('globalMessage.error', t('Erro ao enviar a mensagem') + ': ' + detalhe);
  }
};

/** Como a marca é lida: o que o agente vai fazer com o chamado. */
const rotuloPedidoDeAgente = (pedido: string): string =>
  ({ resolver: 'Marcado para o agente resolver', planejar: 'Marcado para o agente planejar' } as Record<string, string>)[pedido] ?? pedido;

/** Cada pedido como uma escolha: o ícone e o rótulo. O que ele faz mora no catálogo (EX.plan / EX.resolve). */
const ACAO_DO_AGENTE: Record<string, { icone: string; titulo: string }> = {
  planejar: { icone: 'pi pi-map', titulo: 'Planejar' },
  resolver: { icone: 'pi pi-wrench', titulo: 'Resolver' },
};

/**
 * Plan/Resolve are blocked while the ticket already carries a mark: changing
 * the request means taking the mark off first ("Tirar a marca"), so a click
 * never silently swaps what the agent was asked to do.
 */
const agentRequestBlocked = computed((): ExplanationNow | null => {
  const mark = chamadoAberto.value?.agente;
  if (!mark) return null;

  return { kind: 'no', text: `${t(rotuloPedidoDeAgente(mark))} — ${t('tire a marca para trocar o pedido.')}` };
});

const agentRequestExplanation = (request: string): Explanation => {
  const base = request === 'planejar' ? EX.value.plan : request === 'resolver' ? EX.value.resolve : { what: t(rotuloAcaoDeAgente(request)) };

  return agentRequestBlocked.value ? { ...base, now: agentRequestBlocked.value } : base;
};

/** O botão que faz o pedido (rótulo de reserva para um pedido sem descrição). */
const rotuloAcaoDeAgente = (pedido: string): string =>
  ({ resolver: 'Pedir ao agente: resolver', planejar: 'Pedir ao agente: planejar' } as Record<string, string>)[pedido] ?? pedido;

/**
 * Os pedidos na ordem do trabalho — planejar antes de resolver —, seja qual
 * for a ordem em que o backend os mande. Pedido desconhecido vai para o fim.
 */
const ORDEM_DOS_PEDIDOS = ['planejar', 'resolver'];
const pedidosOrdenados = computed((): string[] =>
  [...pedidosDeAgente.value].sort((a, b) => {
    const ia = ORDEM_DOS_PEDIDOS.indexOf(a);
    const ib = ORDEM_DOS_PEDIDOS.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  }),
);

const handleMarcarAgente = async (pedido: string): Promise<void> => {
  const id = chamadoAberto.value?.id;
  if (!id) return;

  try {
    await marcarAgente(id, pedido);
    eventBus.emit('globalMessage.success', t(pedido === '' ? 'Marca do agente retirada' : 'Chamado marcado para o agente'));
    eventBus.emit('chamados.refresh');
  } catch (e) {
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit('globalMessage.error', t('Erro ao marcar para o agente') + ': ' + detalhe);
  }
};

const handleMudarStatus = async (destino: string): Promise<void> => {
  const id = chamadoAberto.value?.id;
  if (!id) return;

  try {
    await mudarStatus(id, destino);
    eventBus.emit('globalMessage.success', t('Status atualizado'));
    eventBus.emit('chamados.refresh');
  } catch (e) {
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit('globalMessage.error', t('Erro ao mudar o status') + ': ' + detalhe);
  }
};

onMounted(() => {
  eventBus.on('modal.chamados.open', handleAbrir);
  eventBus.on('modal.chamados.close', fechar);
});

onBeforeUnmount(() => {
  eventBus.off('modal.chamados.open', handleAbrir);
  eventBus.off('modal.chamados.close', fechar);
});
</script>

<style scoped>
/*
 * Layout de CONVERSA, com a conversa mandando: cabeçalho compacto no topo,
 * balões alinhados por autor com a maior parte da altura, resposta num painel
 * embaixo.
 *
 * A escolha de alinhar por AUTOR (suporte à direita, solicitante à esquerda) é
 * o que faz o fio ser lido como conversa e não como lista: o olho descobre
 * quem falou antes de ler o nome.
 */
.chamados-modal {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ---------- as seções ----------
 * Cada seção tem a mesma anatomia: um topo (título + ações à direita) e o
 * conteúdo. O corpo do Modal da casa é cinza (--app-bg-medium); resumo,
 * conversa e resposta são painéis brancos sobre ele — o mesmo padrão dos
 * `box` dos outros modais —, e o agente fica direto no cinza, como rodapé. */
.secao {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.resumo,
.conversa {
  padding: 16px;
  background: #ffffff;
  border: 1px solid var(--app-border-color);
  border-radius: 8px;
}

.secao-topo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 32px;
}

.secao-topo h4,
.agente-estado h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--app-text-color);
}

.secao-topo h4 i,
.agente-estado h4 i {
  color: var(--app-text-secondary-color);
}


/* ---------- 1. resumo ----------
 * Só o básico do chamado, em três linhas curtas: o assunto (com "ver a tela"),
 * os dados e a trilha do status. O resto da altura é da conversa. */
.resumo {
  gap: 8px;
  padding: 12px 16px;
}

.resumo-linha {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

/* ---------- a trilha do status ---------- */
.ciclo {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ciclo-trilha {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ciclo-passo {
  display: flex;
  align-items: center;
}

/* O traço entre as etapas: a trilha se lê como um caminho, não como botões. */
.ciclo-passo + .ciclo-passo::before {
  content: '';
  width: 14px;
  height: 1px;
  background: var(--app-border-color);
}

.ciclo-etapa {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  font: inherit;
  font-size: 0.78rem;
  white-space: nowrap;
  color: var(--app-text-secondary-color);
  opacity: 0.6;
}

.ciclo-etapa--passada {
  opacity: 1;
}

.ciclo-etapa--passada .pi {
  font-size: 0.65rem;
}

/* Onde o chamado está: cheio, na cor do status (as cores do StatusTag). */
.ciclo-etapa--atual {
  opacity: 1;
  font-weight: 700;
  color: var(--app-text-color);
  background: var(--app-bg-light);
  border-color: var(--app-border-color);
}

.ciclo-etapa--atual.ciclo-etapa--info { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
.ciclo-etapa--atual.ciclo-etapa--warn { background: #fff7ed; border-color: #fed7aa; color: #c2410c; }
.ciclo-etapa--atual.ciclo-etapa--success { background: #f0fdf4; border-color: #bbf7d0; color: #15803d; }
.ciclo-etapa--atual.ciclo-etapa--contrast { background: #1f2937; border-color: #1f2937; color: #ffffff; }

/* Para onde ele pode ir: a um clique, com cara de alvo. */
.ciclo-etapa--disponivel {
  opacity: 1;
  border: 1px dashed var(--app-border-color);
  background: #ffffff;
  color: var(--app-text-color);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, transform 0.15s cubic-bezier(0.22, 1, 0.36, 1);
}

.ciclo-etapa--disponivel:hover {
  border-style: solid;
  border-color: var(--app-primary-color);
  background: rgba(var(--app-primary-color-rgb), 0.08);
  transform: translateY(-1px);
}

.ciclo-etapa--disponivel:focus-visible {
  outline: 2px solid var(--app-primary-color);
  outline-offset: 1px;
}

/* Cancelar é uma saída, não uma etapa: discreto, e só fica vermelho no hover. */
.ciclo-saida {
  margin-left: auto;
  font-size: 0.78rem;
  color: var(--app-text-secondary-color) !important;
}

.ciclo-saida:hover {
  color: var(--app-danger-color) !important;
}

@media (prefers-reduced-motion: reduce) {
  .ciclo-etapa--disponivel,
  .ciclo-etapa--disponivel:hover {
    transition: none;
    transform: none;
  }
}

.chamado-codigo {
  font-family: monospace;
  font-weight: 700;
  color: #555;
}

.chamado-assunto {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--app-text-color);
  overflow-wrap: anywhere;
}

.chamado-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 0.82rem;
  color: #666;
}

.chamado-meta i {
  margin-right: 4px;
}

/* ---------- 3. conversa ----------
 * A janela de conversa: uma moldura só, o fio no fundo cinza com os balões
 * brancos por cima, e a caixa de resposta colada na borda de baixo, separada
 * por uma linha — como num aplicativo de mensagem. */
.chat {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--app-bg-light);
  border: 1px solid var(--app-border-color);
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

/* O foco da caixa acende a janela inteira: é nela que se está escrevendo. */
.chat:has(.resposta:focus-within) {
  border-color: var(--app-primary-color);
  box-shadow: 0 0 0 0.2rem rgba(var(--app-primary-color-rgb), 0.18);
}

.chat:has(.resposta--interna:focus-within) {
  border-color: #ef6c00;
  box-shadow: 0 0 0 0.2rem rgba(239, 108, 0, 0.18);
}

.fio {
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* A conversa tem a maior parte da altura: é o que o atendente lê. */
  max-height: 62vh;
  min-height: 200px;
  overflow-y: auto;
  padding: 16px;
}

.balao-wrapper {
  display: flex;
}

/*
 * O lado é definido por QUEM LÊ ("eu" à direita), não pelo papel de quem
 * escreveu. Ver o docblock de `autor_sou_eu` no contrato: alinhar por papel
 * fazia o franqueado ver as PRÓPRIAS mensagens à esquerda.
 */
.balao-wrapper--outro {
  justify-content: flex-start;
}

.balao-wrapper--eu {
  justify-content: flex-end;
}

.balao {
  max-width: 78%;
  padding: 10px 14px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid var(--app-border-color);
}

.balao--eu {
  background: #e3f2fd;
}

/* Tracejado + fundo âmbar: a nota interna precisa ser DIFERENTE à primeira
   vista, não só rotulada — o atendente varre o fio rápido. */
.balao--interna {
  background: #fff8e1;
  border: 1px dashed #ef6c00;
}

.tarja-interna {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  color: #ef6c00;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.balao-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.balao-autor {
  font-size: 0.8rem;
  font-weight: 700;
  color: #444;
}

.balao-hora {
  font-size: 0.72rem;
  color: #888;
  margin-left: auto;
  white-space: nowrap;
}

/*
  No fio a imagem também aparece do tamanho dela, empilhada — o mesmo que o
  Odoo mostra quando a foto está no corpo da mensagem. Miniatura quadrada
  obrigava a abrir cada uma para saber o que era.
*/
.balao-anexos {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.balao-anexo {
  display: block;
  max-width: 100%;
}

.balao-anexo img {
  max-width: 100%;
  max-height: 360px;
  object-fit: contain;
  display: block;
  border-radius: 5px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.balao-corpo {
  word-break: break-word;
  line-height: 1.45;
  min-width: 0;
}

.balao-corpo--texto {
  white-space: pre-wrap;
}

/* O plano do agente de chamados: títulos, tabelas e o plano recolhido. */
.balao-corpo :deep(h1),
.balao-corpo :deep(h2),
.balao-corpo :deep(h3),
.balao-corpo :deep(h4) {
  margin: 0.9em 0 0.35em;
  line-height: 1.25;
}

.balao-corpo :deep(h1) { font-size: 1.15rem; }
.balao-corpo :deep(h2) { font-size: 1.05rem; }
.balao-corpo :deep(h3),
.balao-corpo :deep(h4) { font-size: 0.95rem; }

.balao-corpo :deep(p),
.balao-corpo :deep(ul),
.balao-corpo :deep(ol) {
  margin: 0.4em 0;
}

.balao-corpo :deep(table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin: 0.5em 0;
  font-size: 0.85rem;
}

.balao-corpo :deep(th),
.balao-corpo :deep(td) {
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 4px 8px;
  text-align: left;
  vertical-align: top;
}

.balao-corpo :deep(th) {
  background: rgba(0, 0, 0, 0.04);
}

.balao-corpo :deep(code) {
  font-size: 0.85em;
  padding: 0 3px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
}

.balao-corpo :deep(pre) {
  overflow-x: auto;
  padding: 8px;
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.05);
}

.balao-corpo :deep(pre code) {
  padding: 0;
  background: none;
}

.balao-corpo :deep(details) {
  margin-top: 0.6em;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding-top: 0.4em;
}

.balao-corpo :deep(summary) {
  cursor: pointer;
  font-weight: 600;
}

.balao-corpo :deep(hr) {
  border: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin: 0.8em 0;
}

.balao-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #fff;
  font-size: 0.72rem;
  flex: 0 0 auto;
}

.balao-avatar--suporte {
  background: #1565c0;
}

.balao-avatar--solicitante {
  background: #78909c;
}

/* ---------- 4. resposta ----------
 * A caixa é o próprio composer (InputRichText), com borda e foco dele; aqui
 * só o que é do chamado: as abas de modo, o botão de sugerir e o tom âmbar
 * da nota interna — o mesmo âmbar tracejado do balão de nota no fio. */
.resposta {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-top: 1px solid var(--app-border-color);
}

/* O composer não tem moldura própria aqui: a moldura é a da janela. */
.resposta :deep(.composer) {
  border: none;
  border-radius: 0;
  background: transparent;
}

.resposta :deep(.composer:focus-within) {
  box-shadow: none;
}

/* A leitura da IA e o aviso de rascunho: uma faixa logo acima da caixa. */
.resposta > .analise,
.resposta > .aviso-sugestao {
  margin: 10px 12px 0;
}

.resposta-modos {
  display: flex;
  gap: 2px;
  padding: 6px 8px 0;
  border-bottom: 1px solid var(--app-border-color);
}

.resposta-modo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px 8px;
  margin-bottom: -1px;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--app-text-secondary-color);
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}

.resposta-modo:hover {
  color: var(--app-text-color);
}

.resposta-modo:focus-visible {
  outline: 2px solid var(--app-primary-color);
  outline-offset: -2px;
  border-radius: 4px;
}

.resposta-modo--ativo {
  color: var(--app-text-color);
  font-weight: 600;
  border-bottom-color: var(--app-primary-color);
}

.resposta-modo--nota.resposta-modo--ativo {
  color: #b45309;
  border-bottom-color: #ef6c00;
}

.resposta-sugerir {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--app-text-secondary-color);
  font: inherit;
  font-size: 0.82rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.resposta-sugerir:hover:not([aria-disabled='true']) {
  background: var(--app-bg-light);
  color: var(--app-text-color);
}

.resposta-sugerir:focus-visible {
  outline: 2px solid var(--app-primary-color);
  outline-offset: 1px;
}

/* Disabled WITH a reason (v-explain makes it aria-disabled): hover still opens why. */
.resposta-sugerir[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Nota interna: a caixa inteira muda de tom, e o botão também. */
.resposta--interna {
  background: #fffbeb;
  border-top: 1px dashed #ef6c00;
}

.resposta--interna :deep(.composer-enviar) {
  background: #ef6c00;
  color: #ffffff;
}

.resposta--interna :deep(.composer-enviar:hover:not(:disabled)) {
  background: #c2410c;
}

/* ---------- 2. agente de chamados ----------
 * Uma linha só, tingida com a primária do tenant: quem é, o que está marcado,
 * o esforço e os pedidos. A explicação de cada pedido mora no catálogo
 * (./explanations.ts), mostrada pelo v-explain. */
.agente {
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px 12px;
  /* Tinta sobre BRANCO: direto sobre o cinza do Modal a cor fica suja. */
  background:
    linear-gradient(rgba(var(--app-primary-color-rgb), 0.1), rgba(var(--app-primary-color-rgb), 0.1)),
    #ffffff;
  border: 1px solid rgba(var(--app-primary-color-rgb), 0.45);
  border-radius: 8px;
  font-size: 0.85rem;
}

.agente-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--app-primary-color);
  color: var(--app-primary-contrast-color);
  font-size: 0.8rem;
}

.agente-nome {
  font-weight: 600;
  color: var(--app-text-color);
}

.agente-marca {
  color: var(--app-text-secondary-color);
}

.agente--marcado .agente-marca {
  color: var(--app-text-color);
}

.agente-esforco {
  padding: 1px 8px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid var(--app-border-color);
  font-size: 0.75rem;
  color: var(--app-text-secondary-color);
  cursor: help;
}

.agente-esforco strong {
  color: var(--app-text-color);
}

.agente-acoes {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.agente-pedido {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  background: #ffffff;
  border: 1px solid var(--app-border-color);
  border-radius: 999px;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--app-text-color);
  cursor: pointer;
  transition: border-color 0.15s, transform 0.15s cubic-bezier(0.22, 1, 0.36, 1);
}

.agente-pedido .pi {
  color: var(--app-text-secondary-color);
  transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.agente-pedido:hover:not([aria-disabled='true']) {
  border-color: var(--app-primary-color);
  transform: translateY(-1px);
}

.agente-pedido:hover:not([aria-disabled='true']) .pi {
  transform: rotate(-12deg) scale(1.1);
}

/* Already marked: disabled with its reason (aria-disabled, so hover explains). */
.agente-pedido[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.55;
}

.agente-pedido:focus-visible {
  outline: 2px solid var(--app-primary-color);
  outline-offset: 1px;
}

@media (prefers-reduced-motion: reduce) {
  .agente-pedido,
  .agente-pedido .pi {
    transition: none;
  }

  .agente-pedido:hover,
  .agente-pedido:hover .pi {
    transform: none;
  }
}

@media (max-width: 768px) {
  .agente-acoes {
    margin-left: 0;
  }

  .balao {
    max-width: 92%;
  }

  .chamado-meta {
    gap: 8px;
  }
}
</style>
